import axios from 'axios';
import type { User, LoginRequest, LoginResponse, ApiError, UpdateEstimateStatusRequest, EstimateStatus } from '@medical-counsellor/shared';

// Create axios instance
const api = axios.create({
    baseURL: import.meta.env.VITE_API_URL || '/api/v1',
    headers: {
        'Content-Type': 'application/json',
    },
});

// Request interceptor - add auth token
api.interceptors.request.use((config) => {
    const token = localStorage.getItem('token');
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
});

// Response interceptor - handle errors
api.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response?.status === 401) {
            // Clear token and redirect to login
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            window.location.href = '/login';
        }
        return Promise.reject(error);
    }
);

// Auth API
export const authApi = {
    login: async (data: LoginRequest): Promise<LoginResponse> => {
        const response = await api.post('/auth/login', data);
        return response.data.data;
    },

    logout: async (): Promise<void> => {
        await api.post('/auth/logout');
        localStorage.removeItem('token');
        localStorage.removeItem('user');
    },

    me: async (): Promise<User> => {
        const response = await api.get('/auth/me');
        return response.data.data;
    },

    changePassword: async (currentPassword: string, newPassword: string): Promise<void> => {
        await api.put('/auth/password', { currentPassword, newPassword });
    },
};

// Hospitals API
export const hospitalsApi = {
    getById: async (id: string) => {
        const response = await api.get(`/hospitals/${id}`);
        return response.data.data;
    },

    update: async (id: string, data: Partial<{ name: string; disclaimerText: string; estimateValidityDays: number }>) => {
        const response = await api.put(`/hospitals/${id}`, data);
        return response.data.data;
    },

    uploadLogo: async (id: string, file: File) => {
        const formData = new FormData();
        formData.append('logo', file);
        const response = await api.post(`/hospitals/${id}/logo`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
        return response.data.data;
    },
};

// Locations API
export const locationsApi = {
    list: async () => {
        const response = await api.get('/locations');
        return response.data.data;
    },

    create: async (data: { name: string; address?: string; city?: string; phone?: string; printHeaderSpacing?: number; printFooterSpacing?: number }) => {
        const response = await api.post('/locations', data);
        return response.data.data;
    },

    update: async (id: string, data: Partial<{ name: string; address: string; city: string; phone: string; printHeaderSpacing: number; printFooterSpacing: number }>) => {
        const response = await api.put(`/locations/${id}`, data);
        return response.data.data;
    },
};

// Procedures API
export const proceduresApi = {
    list: async (locationId?: string) => {
        const params = locationId ? { locationId } : {};
        const response = await api.get('/procedures', { params });
        return response.data.data;
    },

    create: async (data: {
        code: string;
        name: string;
        category?: string;
        description?: string;
        defaultStayDays?: number;
        defaultIcuDays?: number;
    }) => {
        const response = await api.post('/procedures', data);
        return response.data.data;
    },

    update: async (id: string, data: Partial<{ code: string; name: string; category: string; description: string; defaultStayDays: number; defaultIcuDays: number }>) => {
        const response = await api.put(`/procedures/${id}`, data);
        return response.data.data;
    },

    configure: async (id: string, locationId: string, isActive: boolean = true) => {
        const response = await api.post(`/procedures/${id}/config`, { locationId, isActive });
        return response.data.data;
    },
};

// Cost Sections API
export const costSectionsApi = {
    list: async (locationId: string) => {
        const response = await api.get('/cost-sections', { params: { locationId } });
        return response.data.data;
    },

    create: async (data: {
        locationId: string;
        name: string;
        sectionType: 'FIXED' | 'PERCENTAGE';
        displayOrder?: number;
        isRequired?: boolean;
    }) => {
        const response = await api.post('/cost-sections', data);
        return response.data.data;
    },

    update: async (id: string, data: Partial<{ name: string; sectionType: string; displayOrder: number; isRequired: boolean }>) => {
        const response = await api.put(`/cost-sections/${id}`, data);
        return response.data.data;
    },

    delete: async (id: string) => {
        await api.delete(`/cost-sections/${id}`);
    },

    reorder: async (locationId: string, sectionIds: string[]) => {
        const response = await api.put('/cost-sections/reorder', { locationId, sectionIds });
        return response.data;
    },
};

// Cost Items API
export const costItemsApi = {
    list: async (sectionId: string) => {
        const response = await api.get('/cost-items', { params: { sectionId } });
        return response.data.data;
    },

    create: async (data: {
        costSectionId: string;
        name: string;
        unit: 'PER_DAY' | 'PER_PROCEDURE' | 'PER_UNIT';
        isSelectable?: boolean;

        exclusiveGroup?: string;
        defaultAmount?: number;
        defaultPercentage?: number;
        defaultUnitPrice?: number;
    }) => {
        const response = await api.post('/cost-items', data);
        return response.data.data;
    },

    update: async (id: string, data: Partial<{ name: string; unit: string; isSelectable: boolean; exclusiveGroup: string; defaultAmount: number; defaultPercentage: number; defaultUnitPrice: number }>) => {
        const response = await api.put(`/cost-items/${id}`, data);
        return response.data.data;
    },

    delete: async (id: string) => {
        await api.delete(`/cost-items/${id}`);
    },
};

// Pricing API
export const pricingApi = {
    get: async (procedureConfigId: string) => {
        const response = await api.get(`/pricing/${procedureConfigId}`);
        return response.data.data;
    },

    update: async (procedureConfigId: string, items: Array<{
        costItemId: string;
        amount?: number | null;
        percentage?: number | null;
        roomTypeOverrides?: Array<{ roomType: string; amount?: number; percentage?: number }>;
    }>) => {
        const response = await api.put(`/pricing/${procedureConfigId}`, { items });
        return response.data.data;
    },

    clone: async (sourceConfigId: string, targetConfigId: string) => {
        const response = await api.post('/pricing/clone', { sourceConfigId, targetConfigId });
        return response.data.data;
    },

    exportExcel: async (locationId: string): Promise<Blob> => {
        const response = await api.get(`/pricing/export/${locationId}`, {
            responseType: 'blob',
        });
        return response.data;
    },

    importPreview: async (locationId: string, file: File) => {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('locationId', locationId);
        const response = await api.post('/pricing/import/preview', formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
        return response.data.data;
    },

    importApply: async (importToken: string, locationId: string) => {
        const response = await api.post('/pricing/import/apply', { importToken, locationId });
        return response.data.data;
    },
};

// Room Types API
export const roomTypesApi = {
    list: async (locationId: string) => {
        const response = await api.get('/room-types', { params: { locationId } });
        return response.data.data;
    },

    create: async (data: {
        locationId: string;
        name: string;
        description?: string;
        defaultRate: number;
        markupPercentage?: number;
        displayOrder?: number;
    }) => {
        const response = await api.post('/room-types', data);
        return response.data.data;
    },

    update: async (id: string, data: Partial<{
        name: string;
        description: string;
        defaultRate: number;
        markupPercentage: number;
        displayOrder: number;
    }>) => {
        const response = await api.put(`/room-types/${id}`, data);
        return response.data.data;
    },

    delete: async (id: string) => {
        await api.delete(`/room-types/${id}`);
    },

    getOverrides: async (procedureConfigId: string) => {
        const response = await api.get(`/room-types/${procedureConfigId}/overrides`);
        return response.data.data;
    },

    setOverrides: async (procedureConfigId: string, overrides: Array<{
        procedureConfigId: string;
        roomTypeId: string;
        overrideRate: number;
    }>) => {
        const response = await api.put(`/room-types/${procedureConfigId}/overrides`, { overrides });
        return response.data.data;
    },

    deleteOverride: async (procedureConfigId: string, roomTypeId: string) => {
        await api.delete(`/room-types/${procedureConfigId}/overrides/${roomTypeId}`);
    },
};

// ICU Rates API
export const icuRatesApi = {
    list: async (locationId: string) => {
        const response = await api.get('/icu-rates', { params: { locationId } });
        return response.data.data;
    },

    create: async (data: {
        locationId: string;
        name: string;
        description?: string;
        dailyRate: number;
        displayOrder?: number;
    }) => {
        const response = await api.post('/icu-rates', data);
        return response.data.data;
    },

    update: async (id: string, data: Partial<{
        name: string;
        description: string;
        dailyRate: number;
        displayOrder: number;
    }>) => {
        const response = await api.put(`/icu-rates/${id}`, data);
        return response.data.data;
    },

    delete: async (id: string) => {
        await api.delete(`/icu-rates/${id}`);
    },

    getOverrides: async (procedureConfigId: string) => {
        const response = await api.get(`/icu-rates/${procedureConfigId}/overrides`);
        return response.data.data;
    },

    setOverrides: async (procedureConfigId: string, overrides: Array<{
        procedureConfigId: string;
        icuRateId: string;
        overrideRate: number;
    }>) => {
        const response = await api.put(`/icu-rates/${procedureConfigId}/overrides`, { overrides });
        return response.data.data;
    },

    deleteOverride: async (procedureConfigId: string, icuRateId: string) => {
        await api.delete(`/icu-rates/${procedureConfigId}/overrides/${icuRateId}`);
    },
};

// Estimates API
export const estimatesApi = {
    calculate: async (data: {
        procedureConfigId: string;
        stayDays: number;
        roomType: string;
        icuDays?: number;
        icuRateId?: string;
        selectedConditions?: string[];
        selectedItems?: string[];
        itemQuantities?: Record<string, number>;
    }) => {
        const response = await api.post('/estimates/calculate', data);
        return response.data.data;
    },

    create: async (data: {
        procedureConfigId: string;
        patientName: string;
        patientId?: string;
        stayDays: number;
        roomType: string;
        icuDays?: number;
        icuRateId?: string;
        selectedConditions?: string[];
        selectedItems?: string[];
        itemQuantities?: Record<string, number>;
        doctors?: Array<{ id: string; isPrimary: boolean }>;
    }) => {
        const response = await api.post('/estimates', data);
        return response.data.data;
    },

    createCustom: async (data: {
        patientName: string;
        patientId?: string;
        locationId: string;
        roomType: string;
        stayDays: number;
        procedureName?: string;
        items: Array<{
            name: string;
            quantity: number;
            unitPrice: number;
        }>;
        doctors?: Array<{ id: string; isPrimary: boolean }>;
    }) => {
        const response = await api.post('/estimates/custom', data);
        return response.data.data;
    },

    list: async (locationId?: string, page: number = 1, pageSize: number = 20) => {
        const params = { locationId, page, pageSize };
        const response = await api.get('/estimates', { params });
        return response.data;
    },

    getById: async (id: string) => {
        const response = await api.get(`/estimates/${id}`);
        return response.data.data;
    },

    getPdf: async (id: string) => {
        const response = await api.get(`/estimates/${id}/pdf`, { responseType: 'blob' });
        return response.data;
    },

    updateStatus: async (id: string, data: UpdateEstimateStatusRequest) => {
        const response = await api.patch(`/estimates/${id}/status`, data);
        return response.data.data;
    },

    convertToActual: async (id: string) => {
        const response = await api.post(`/estimates/${id}/convert`);
        return response.data.data;
    },

    saveActuals: async (id: string, data: {
        admissionId?: string;
        surgeryDate?: string;
        dischargeDate?: string;
        actualStayDays?: number;
        actualRoomType?: string;
        billingReference?: string;
        lines: Array<{
            sectionName?: string;
            itemName: string;
            quantity: number;
            unitPrice: number;
        }>;
    }) => {
        const response = await api.post(`/estimates/${id}/actual`, data);
        return response.data.data;
    },

    getVariance: async (id: string) => {
        const response = await api.get(`/estimates/${id}/variance`);
        return response.data.data;
    },

    saveVariance: async (id: string, data: {
        estimatedTotal: number;
        actualTotal: number;
        varianceAmount: number;
        variancePercent: number;
        varianceReasons: Array<{
            category: string;
            amount: number;
            notes?: string;
        }>;
    }) => {
        const response = await api.post(`/estimates/${id}/variance`, data);
        return response.data.data;
    },

    getVarianceCategories: async () => {
        const response = await api.get('/estimates/variance-categories');
        return response.data.data;
    },
};

// Users API
export const usersApi = {
    list: async () => {
        const response = await api.get('/users');
        return response.data.data;
    },

    create: async (data: {
        username: string;
        password: string;
        fullName: string;
        email?: string;
        role: 'admin' | 'staff';
        locationIds?: string[];
    }) => {
        const response = await api.post('/users', data);
        return response.data.data;
    },

    update: async (id: string, data: Partial<{ fullName: string; email: string; role: string; locationIds: string[]; isActive: boolean }>) => {
        const response = await api.put(`/users/${id}`, data);
        return response.data.data;
    },

    delete: async (id: string) => {
        await api.delete(`/users/${id}`);
    },

    resetPassword: async (id: string, newPassword: string) => {
        await api.post(`/users/${id}/reset-password`, { newPassword });
    },
};

// Doctors API
export const doctorsApi = {
    list: async (hospitalId?: string) => {
        const params = hospitalId ? { hospitalId } : {};
        const response = await api.get('/doctors', { params });
        return response.data.data;
    },

    create: async (data: {
        name: string;
        qualification?: string;
        registrationNumber?: string;
        userId?: string;
        locationIds?: string[];
        status?: 'ACTIVE' | 'INACTIVE';
    }) => {
        const response = await api.post('/doctors', data);
        return response.data.data;
    },

    update: async (id: string, data: Partial<{
        name: string;
        qualification: string;
        registrationNumber: string;
        userId: string;
        locationIds: string[];
        status: 'ACTIVE' | 'INACTIVE';
    }>) => {
        const response = await api.put(`/doctors/${id}`, data);
        return response.data.data;
    },

    delete: async (id: string) => {
        await api.delete(`/doctors/${id}`);
    },
};

export default api;
